import { ArrowRight, LockKeyhole, Minus, Plus, Trash2 } from 'lucide-react';
import { Link } from 'react-router-dom';
import EmptyState from '../../components/common/EmptyState.jsx';
import Seo from '../../components/common/Seo.jsx';
import { useAppData } from '../../contexts/AppDataContext.jsx';
import { useCart } from '../../contexts/CartContext.jsx';
import { assetUrl } from '../../services/storefrontService.js';

export default function CartPage() {
  const { data } = useAppData();
  const { cart, updateItem, removeItem, clearCart } = useCart();
  const productsHref = data?.navigation?.[0]?.href || data?.home?.categories?.[0]?.href || '/';
  const itemLabel = `${cart.quantity} item${cart.quantity === 1 ? '' : 's'}`;

  if (!cart.items.length) {
    return (
      <section className="container cart-page cart-page-empty">
        <Seo title="Shopping Cart" path="/cart" noindex />
        <EmptyState title="Your cart is empty" text="The hive is waiting - add a few jars to start a checkout." />
        <div className="center-actions">
          <Link className="button button-honey" to={productsHref}>Browse Products <ArrowRight size={17} /></Link>
        </div>
      </section>
    );
  }

  return (
    <section className="container cart-page">
      <Seo title="Shopping Cart" path="/cart" noindex />
      <div className="cart-header-row">
        <h1>Cart</h1>
        <button className="cart-clear-button" type="button" onClick={clearCart} aria-label="Clear cart">
          <Trash2 size={16} />
        </button>
      </div>

      <div className="cart-layout">
        <div className="cart-list" aria-label={`${itemLabel} in cart`}>
          {cart.items.map((item) => (
            <article className="cart-line" key={item.lineKey}>
              <Link className="cart-line-media" to={`/product/${item.productId}`} aria-label={`View ${item.name}`}>
                <img src={assetUrl(item.image)} alt={item.name} />
              </Link>
              <div className="cart-line-copy">
                <Link className="cart-line-title" to={`/product/${item.productId}`}>{item.name}</Link>
                <p>{[item.sizeName, item.colorName].filter(Boolean).join(' / ') || 'Default option'}</p>
              </div>
              <div className="quantity-control cart-quantity-control">
                <button type="button" onClick={() => updateItem({ ...item, quantity: Math.max(1, item.quantity - 1) })} aria-label="Decrease quantity"><Minus size={15} /></button>
                <span>{item.quantity}</span>
                <button type="button" onClick={() => updateItem({ ...item, quantity: item.quantity + 1 })} aria-label="Increase quantity"><Plus size={15} /></button>
              </div>
              <strong className="cart-line-price">${item.subtotal.toFixed(2)}</strong>
              <button className="cart-remove-button" type="button" onClick={() => removeItem(item)} aria-label={`Remove ${item.name}`}>
                <Trash2 size={18} />
              </button>
            </article>
          ))}
        </div>

        <aside className="summary-card cart-summary-card">
          <h2>Order Summary</h2>
          <div className="summary-row"><span>Item Count</span><strong>{cart.quantity}</strong></div>
          <div className="summary-row"><span>Subtotal</span><strong>${cart.subtotal.toFixed(2)}</strong></div>
          <div className="summary-row"><span>Shipping</span><strong>Calculated at checkout</strong></div>
          <div className="summary-row summary-total"><span>Total</span><strong>${cart.subtotal.toFixed(2)}</strong></div>
          <Link className="cart-checkout-button" to="/checkout"><LockKeyhole size={15} /> Checkout <ArrowRight size={16} /></Link>
          <Link className="cart-continue-button" to={productsHref}>Continue Shopping</Link>
        </aside>
      </div>

      <div className="cart-mobile-bar" aria-label="Cart checkout">
        <div>
          <span>Total</span>
          <strong>${cart.subtotal.toFixed(2)}</strong>
        </div>
        <Link className="cart-mobile-checkout" to="/checkout"><LockKeyhole size={15} /> Proceed to Checkout <ArrowRight size={16} /></Link>
      </div>
    </section>
  );
}
